/**
 * Client-side mirror of services/employeeReplacementCost.js for defensive recompute.
 * API-enriched employees should include estimatedReplacementCost / replacementCostDetail.
 */

const WORKING_DAYS_PER_YEAR = 260;
const RECRUITING_RATE = 0.15;
const ONBOARDING_RATE = 0.1;

const ROLE_MULTIPLIERS = {
  Entry: 0.3,
  Mid: 0.5,
  Manager: 1.0,
  Executive: 1.5,
};

const DEFAULT_VACANCY_DAYS_BY_SENIORITY = {
  Entry: 25,
  Mid: 35,
  Manager: 45,
  Executive: 60,
};

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

function computeReplacementCost({ annualSalary, seniorityLevel, vacancyDays, productivityImpact }) {
  const roleMultiplier = ROLE_MULTIPLIERS[seniorityLevel];
  if (roleMultiplier == null) throw new Error(`Invalid seniorityLevel: ${seniorityLevel}`);
  const recruitingCost = annualSalary * RECRUITING_RATE;
  const onboardingCost = annualSalary * ONBOARDING_RATE;
  const daily = annualSalary / WORKING_DAYS_PER_YEAR;
  const vacancyCost = daily * vacancyDays;
  const lostProductivityCost = daily * vacancyDays * productivityImpact;
  const replacementCost =
    annualSalary * roleMultiplier + recruitingCost + onboardingCost + vacancyCost + lostProductivityCost;
  return {
    roleMultiplier,
    recruitingCost: roundMoney(recruitingCost),
    onboardingCost: roundMoney(onboardingCost),
    vacancyCost: roundMoney(vacancyCost),
    lostProductivityCost: roundMoney(lostProductivityCost),
    replacementCost: roundMoney(replacementCost),
  };
}

function firstPositiveNumber(values) {
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function resolveAnnualSalary(employee) {
  const fromComp = firstPositiveNumber([
    employee.salary,
    employee.annualSalary,
    employee.compensation,
    employee.baseSalary,
  ]);
  if (fromComp != null) return { amount: fromComp, source: 'compensation_fields' };
  const market = Number(employee.marketSalary);
  if (Number.isFinite(market) && market > 0) return { amount: market, source: 'marketSalary_fallback' };
  return { amount: null, source: 'unavailable' };
}

function normalizeSeniorityToken(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const i = Math.round(raw);
    if (i === 1) return 'Entry';
    if (i === 2) return 'Mid';
    if (i === 3) return 'Manager';
    if (i === 4) return 'Executive';
  }
  const s = String(raw).trim().toLowerCase();
  if (s === 'entry' || s === 'junior' || s === 'intern') return 'Entry';
  if (s === 'mid' || s === 'middle' || s === 'intermediate') return 'Mid';
  if (s === 'manager' || s === 'management') return 'Manager';
  if (s === 'executive' || s === 'exec') return 'Executive';
  return null;
}

function inferSeniorityFromRole(role) {
  const r = String(role || '').toLowerCase();
  if (!r.trim()) return { level: 'Mid', source: 'default' };
  if (
    /\b(chief|ceo|cfo|cto|coo|president|chairman|founder|partner)\b/.test(r) ||
    /\b(evp|svp)\b/.test(r) ||
    /\bvice\s+president\b/.test(r) ||
    /\bvp\b/.test(r)
  ) {
    return { level: 'Executive', source: 'inferred_title' };
  }
  if (
    /\b(manager|director|supervisor)\b/.test(r) ||
    /\bhead\s+of\b/.test(r) ||
    /\blead\b/.test(r)
  ) {
    return { level: 'Manager', source: 'inferred_title' };
  }
  if (/\b(intern|junior|jr\.?|entry|associate i)\b/.test(r) || /\bassistant\b/.test(r)) {
    return { level: 'Entry', source: 'inferred_title' };
  }
  return { level: 'Mid', source: 'inferred_title' };
}

function resolveSeniorityLevel(employee) {
  const explicit =
    normalizeSeniorityToken(employee.seniorityLevel) || normalizeSeniorityToken(employee.level);
  if (explicit) return { level: explicit, source: 'employee_field' };
  const fromResume = normalizeSeniorityToken(employee.resumeProfile?.seniorityLevel);
  if (fromResume) return { level: fromResume, source: 'resumeProfile' };
  return inferSeniorityFromRole(employee.role);
}

function resolveVacancyDays(employee, seniorityLevel) {
  const keys = [
    employee.vacancyDays,
    employee.timeToFillDays,
    employee.timeToFill,
    employee.estimatedVacancyDays,
  ];
  for (const c of keys) {
    const n = Number(c);
    if (Number.isFinite(n) && n >= 0) return { days: n, source: 'employee_field' };
  }
  const def = DEFAULT_VACANCY_DAYS_BY_SENIORITY[seniorityLevel] ?? DEFAULT_VACANCY_DAYS_BY_SENIORITY.Mid;
  return { days: def, source: 'default_by_seniority' };
}

function clampProductivity(p) {
  return Math.min(1, Math.max(0.25, p));
}

function resolveProductivityImpact(employee) {
  const p = Number(employee.productivityImpact);
  if (Number.isFinite(p) && p >= 0.25 && p <= 1) {
    return { value: p, source: 'employee_field' };
  }
  const perf = Number(employee.performanceScore);
  if (Number.isFinite(perf)) {
    const t = clampProductivity(0.25 + (perf / 100) * 0.75);
    return { value: t, source: 'performanceScore' };
  }
  return { value: 0.5, source: 'default' };
}

export function getReplacementCostEstimateForEmployee(employee) {
  const { amount: annualSalary, source: salarySource } = resolveAnnualSalary(employee);
  if (annualSalary == null) {
    return { estimatedReplacementCost: null, replacementCostDetail: null };
  }
  const { level: seniorityLevel, source: senioritySource } = resolveSeniorityLevel(employee);
  const { days: vacancyDays, source: vacancySource } = resolveVacancyDays(employee, seniorityLevel);
  const { value: productivityImpact, source: productivitySource } = resolveProductivityImpact(employee);
  const costs = computeReplacementCost({
    annualSalary,
    seniorityLevel,
    vacancyDays,
    productivityImpact,
  });
  const roleBasedCost = roundMoney(annualSalary * costs.roleMultiplier);
  return {
    estimatedReplacementCost: costs.replacementCost,
    replacementCostDetail: {
      replacementCost: costs.replacementCost,
      roleBasedCost,
      roleMultiplier: costs.roleMultiplier,
      recruitingCost: costs.recruitingCost,
      onboardingCost: costs.onboardingCost,
      vacancyCost: costs.vacancyCost,
      lostProductivityCost: costs.lostProductivityCost,
      inputsUsed: {
        annualSalary,
        seniorityLevel,
        vacancyDays,
        productivityImpact,
      },
      sources: {
        annualSalary: salarySource,
        seniority: senioritySource,
        vacancy: vacancySource,
        productivity: productivitySource,
      },
    },
  };
}

/** Human-readable labels for `replacementCostDetail.sources` keys */
export const SOURCE_LABELS = {
  compensation_fields: 'Salary on file',
  marketSalary_fallback: 'Market salary (fallback)',
  unavailable: 'Unavailable',
  employee_field: 'From profile',
  resumeProfile: 'Resume profile',
  inferred_title: 'Inferred from title',
  default: 'Default',
  default_by_seniority: 'Default for seniority',
};

export function describeSource(key) {
  return SOURCE_LABELS[key] || key;
}

/**
 * Marketing / education estimate from annual salary alone (defaults to Mid seniority).
 */
export function estimateReplacementCostForSalary(
  annualSalary,
  { seniorityLevel = 'Mid', productivityImpact = 0.5 } = {},
) {
  const salary = Number(annualSalary);
  if (!Number.isFinite(salary) || salary <= 0) return null;
  const vacancyDays = DEFAULT_VACANCY_DAYS_BY_SENIORITY[seniorityLevel]
    ?? DEFAULT_VACANCY_DAYS_BY_SENIORITY.Mid;
  return computeReplacementCost({
    annualSalary: salary,
    seniorityLevel,
    vacancyDays,
    productivityImpact,
  }).replacementCost;
}
